<template>
  <div class="chat-widget__footer-drop-area" :class="{ 'dragover': isDragOver }"
       @dragover.prevent="handleDragOver"
       @dragleave.prevent="handleDragLeave"
       @drop.prevent="handleDrop"
  >
    <TokenStat :input-value="userInput.inputValue" :limit="appState.inputLimit" :is-error="limitExceeded()"/>
    <div v-if="chatStore.error" v-html="chatStore.error" style="padding-bottom: 5px"></div>
    <div class="chat-widget__footer-input-area">
      <FileAttachment class="chat-widget__attachment-button"
                      @add-files="handleAddFiles"
                      v-if="appState.attachFiles"
                      :accept="inputAccept"
                      :multiple="!appState.limitFiles"/>
      <div class="chat-widget__input-wrapper">
      <textarea
          rows="1"
          ref="textarea"
          class="chat-widget__input"
          :placeholder="inputPlaceholder"
          :disabled="appState.disableAll"
          @input="handleInput"
          v-model="userInput.inputValue"
          @keydown="handleKeyDown"
          v-focus="!appState.initial && !appState.authorized"
      />
      </div>
      <SendButton
          :disabled="isButtonDisabled"
          :initial="appState.initial"
          :block-sending="appState.blockSending"
          @send="handleSending"
      />
    </div>
    <ExtendInformation v-if="appState.showExtendInfo"/>
    <FileAttachmentList
        :files="attachedFiles"
        @remove-file="handleRemoveFile"
    />
  </div>
</template>
<script setup>
import {computed, ref} from "vue";
import {useInputStore} from '@/modules/chat/stores/userInputStore';
import {useAppStateStore} from '@/modules/chat/stores/appStore';
import {useChatStore} from '@/modules/chat/stores/chatStore';
import {useToastStore} from "@/modules/chat/stores/toast";
import {useMessageHistory} from "@/modules/chat/composables/useMessageHistory";
import {formAlert} from "@/services/alert";
import {ALERT_ERROR_CLASSNAME, MODE_IMAGE} from "@/constants";
import ExtendInformation from "@/modules/chat/components/footer/ExtendInformation.vue";
import FileAttachment from "@/modules/chat/components/files/FileAttachment.vue";
import FileAttachmentList from "@/modules/chat/components/files/FileAttachmentList.vue";
import SendButton from "@/modules/chat/components/footer/SendButton.vue";
import TokenStat from "@/modules/chat/components/footer/TokenStat.vue";

const userInput = useInputStore();
const appState = useAppStateStore();
const chatStore = useChatStore();
const toastStore = useToastStore();

const textarea = ref(null);
const maxLines = computed(() => appState.showMusicFields ? 20: 7);
const lineHeight = 20; // in pixels

const adjustRows = () => {
  if (textarea.value) {
    textarea.value.style.height = 'auto'; // Reset height
    const {scrollHeight} = textarea.value;
    const maxHeight = maxLines.value * lineHeight;
    textarea.value.style.height = `${Math.min(scrollHeight, maxHeight)}px`; // Set new height
  } else {
    textarea.value.style.height = `${lineHeight}px`;
  }
};

const vFocus = {
  mounted: (el, binding) => {
    if (binding.value) {
      el.focus();
    }
  },
  updated: (el, binding) => {
    if (binding.value) {
      el.focus();
    }
  }
};

const limitExceeded = () => appState.inputLimit !== null && userInput.inputValue.length > appState.inputLimit;

const inputAccept = computed(() => appState.mode === MODE_IMAGE ? 'image/png, image/jpeg' : '*/*');

const inputPlaceholder = computed(() => appState.showMusicFields ? 'Введите текст песни' : 'Введите ваш запрос');

const isButtonDisabled = computed(() =>
    appState.disableAll ||
    appState.blockSending ||
    limitExceeded()
);

const {
  // eslint-disable-next-line no-unused-vars
  isViewingHistory,
  navigateUp,
  navigateDown,
  exitHistoryView,
  resetHistoryView
} = useMessageHistory(chatStore, userInput, adjustRows);

const handleKeyDown = async (event) => {
  // Проверяем, была ли нажата комбинация Ctrl + Enter
  if (event.ctrlKey && event.key === 'Enter') {
    event.preventDefault(); // Предотвращаем стандартное поведение
    if (!isButtonDisabled.value) { // Проверяем, не заблокирована ли отправка
      // eslint-disable-next-line no-use-before-define
      await handleSending();
      resetHistoryView();
    }
  }

  // Обработка кнопки "вверх"
  if (event.key === 'ArrowUp') {
    if (!isViewingHistory.value) {
      return;
    }

    event.preventDefault();
    navigateUp();
  }
  // Обработка кнопки "вниз"
  else if (event.key === 'ArrowDown') {
    if (!isViewingHistory.value) {
      return;
    }

    event.preventDefault();
    navigateDown();
  }
  // Если нажата любая другая клавиша, выходим из режима просмотра истории
  else if (!userInput.inputValue.trim()) {
    resetHistoryView();
  } else {
    exitHistoryView();
  }
};

const handleInput = () => {
  adjustRows();
  if (!userInput.inputValue.trim()) {
    resetHistoryView();
  } else {
    exitHistoryView();
  }
  chatStore.setError(null);
};

const attachedFiles = ref([]);

// Обработчики событий файлов
const handleAddFiles = (newFiles) => {
  if (appState.limitFiles) {
    // Если есть лимит, заменяем все файлы на новый
    attachedFiles.value = newFiles;
  } else {
    // Если лимита нет, добавляем к существующим
    attachedFiles.value = [...attachedFiles.value, ...newFiles];
  }
};
const handleRemoveFile = (index) => {
  attachedFiles.value = attachedFiles.value.filter((_, i) => i !== index);
};
const handleClearFiles = () => {
  attachedFiles.value = [];
};

const handleSending = async () => {
  if (!userInput.inputValue.trim() && !attachedFiles.value.length) {
    chatStore.setError(await formAlert('Пожалуйста, введите запрос', ALERT_ERROR_CLASSNAME));
    return;
  }
  chatStore.setError(null);

  const files = attachedFiles.value.length ? Array.from(attachedFiles.value) : null;
  const result = await appState.handleSending(files);
  if (result !== false && attachedFiles.value.length) {
    handleClearFiles();
  }
  if (result !== false) {
    await userInput.resetInputValue();
    adjustRows();
  }
};
const isDragOver = ref(false);
const handleDragOver = () => {
  if (!appState.attachFiles) {
    return;
  }
  isDragOver.value = true;
};
const handleDragLeave = (event) => {
  if (!appState.attachFiles) {
    return;
  }
  if (event.target === event.currentTarget) {
    isDragOver.value = false;
  }
};
const handleDrop = (event) => {
  if (!appState.attachFiles) {
    return;
  }
  isDragOver.value = false;

  let files = Array.from(event.dataTransfer.files);

  // Если установлен лимит на один файл, берем только первый файл
  if (appState.limitFiles) {
    const showAlert = files.length > 1;
    files = files.slice(0, 1);
    if (showAlert) {
    toastStore.showSecondary('В режиме генерации изображений можно прикрепить только один файл. Все остальные будут проигнорированы.');
    }
  }

  handleAddFiles(files);
};

</script>